import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Eye, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllSecurityIncidents,
  getSecurityIncidentById,
} from "../../../service/securityService";
import {
  SecuritySearchFilters,
  PaginationParams,
  IncidentSeverity,
  SecurityIncidentFormData,
  SecurityIncident,
} from "../../../types/security_types";
import { useOrgUsersStore } from "../../../store/store";
import { Attachment } from "../../../types/api";
import SecurityIncidentForm from "./SecurityIncidentForm";
import SecurityInvestigationPanel from "./SecurityInvestigationPanel";
import SecurityEscalationMatrix from "./SecurityEscalationMatrix";
import SecurityPanicTriggers from "./SecurityPanicTriggers";

function mapSecurityIncidentToFormData(inc: SecurityIncident): SecurityIncidentFormData {
  const raw = inc.incidentDateTime || inc.createdAt || "";
  let dateTime = new Date().toISOString().slice(0, 16);
  if (raw) {
    try {
      dateTime = new Date(raw).toISOString().slice(0, 16);
    } catch {
      /* keep default */
    }
  }

  const esc = inc.escalatedTo;
  const escalatedTo = Array.isArray(esc)
    ? esc.map(String)
    : esc != null && String(esc).trim()
      ? [String(esc).trim()]
      : [];

  return {
    incidentType: inc.incidentType,
    location: inc.location,
    zoneId: typeof inc.zone === "string" ? inc.zone : undefined,
    dateTime,
    severity: inc.severity,
    description: inc.description,
    immediateActionTaken: inc.immediateActionTaken || "",
    escalatedTo,
    linkedLogRefs: inc.linkedLogRefs || [],
    evidenceUrls: inc.evidence || [],
  };
}

function evidenceToAttachments(urls: string[] | undefined): Attachment[] {
  if (!urls?.length) return [];
  return urls.map((url, i) => ({
    url,
    name: `Evidence ${i + 1}`,
  }));
}

const SecurityIncidents: React.FC = () => {
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const [tab, setTab] = useState<"incidents" | "matrix">("incidents");
  const [rows, setRows] = useState<SecurityIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] = useState<string>("ALL Premises");
  const [severityFilter, setSeverityFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailIncident, setDetailIncident] = useState<SecurityIncident | null>(null);
  const [pagination] = useState<PaginationParams>({
    page: 1,
    limit: 100,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (searchQuery.trim()) filters.searchQuery = searchQuery.trim();
      if (severityFilter !== "ALL") filters.severity = severityFilter;
      if (statusFilter !== "ALL") filters.status = statusFilter;

      const res = await getAllSecurityIncidents(filters, pagination);
      let list = res.data || [];

      if (selectedRegion !== "ALL Regions") {
        list = list.filter((inc) => {
          const r = (inc as any).region;
          return r === selectedRegion;
        });
      }
      if (selectedPremises !== "ALL Premises") {
        list = list.filter((inc) => {
          const p = (inc as any).premiseName || (inc as any).hospitalName;
          return p === selectedPremises;
        });
      }

      setRows(list);
    } catch (error: any) {
      console.error("Error fetching security incidents:", error);
      toast.error(error.response?.data?.message || "Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, [
    searchQuery,
    selectedRegion,
    selectedPremises,
    severityFilter,
    statusFilter,
    pagination,
  ]);

  useEffect(() => {
    fetchOrgUsers();
  }, [fetchOrgUsers]);

  useEffect(() => {
    if (tab === "incidents") load();
  }, [tab, load]);

  useEffect(() => {
    const handleIncidentCreated = () => {
      load();
    };
    window.addEventListener("incidentCreated", handleIncidentCreated);
    return () => {
      window.removeEventListener("incidentCreated", handleIncidentCreated);
    };
  }, [load]);

  const openDetail = async (inc: SecurityIncident) => {
    const sid = inc.id || inc._id;
    if (!sid) return;
    try {
      setDetailLoading(true);
      setIsDetailOpen(true);
      const full = await getSecurityIncidentById(String(sid));
      setDetailIncident(full);
    } catch (e) {
      console.error(e);
      toast.error("Could not load incident");
      setIsDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const getSeverityBadge = (severity: IncidentSeverity) => {
    const colors: Record<IncidentSeverity, string> = {
      Low: "bg-green-100 text-green-800",
      Medium: "bg-yellow-100 text-yellow-800",
      High: "bg-orange-100 text-orange-800",
      Critical: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          colors[severity] || "bg-gray-100 text-gray-800"
        }`}
      >
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status?: string) => {
    const colors: Record<string, string> = {
      Open: "bg-red-100 text-red-800",
      "In Progress": "bg-blue-100 text-blue-800",
      "Under Investigation": "bg-yellow-100 text-yellow-800",
      Resolved: "bg-[#EAFBFA] text-[#0fa9a6]",
      Closed: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          colors[status || ""] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status || "Open"}
      </span>
    );
  };

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Incident &amp; emergency management (Security)
              </h1>
              <p className="text-gray-600 text-sm max-w-3xl">
                Security register with severity rules, evidence, escalation, panic triggers, and
                investigation workflow. New reports sync to the{" "}
                <Link to="/incidents" className="text-[#0fa9a6] font-medium hover:underline">
                  organization incident module
                </Link>{" "}
                (department Security) when sync succeeds.
              </p>
            </div>
          </div>
          {tab === "incidents" && (
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Report incident
            </button>
          )}
        </div>

        <div className="flex gap-2 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setTab("incidents")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === "incidents"
                ? "border-[#0fa9a6] text-[#0fa9a6]"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            Incidents
          </button>
          <button
            type="button"
            onClick={() => setTab("matrix")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === "matrix"
                ? "border-[#0fa9a6] text-[#0fa9a6]"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            Escalation matrix
          </button>
        </div>
      </div>

      {tab === "matrix" ? (
        <SecurityEscalationMatrix />
      ) : (
        <>
          <div className="mb-4">
            <SecurityPanicTriggers onCreated={() => load()} />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search type, location, number…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>
              <div className="min-w-[150px]">
                <Dropdown
                  selectedValue={selectedRegion}
                  onChange={setSelectedRegion}
                  type="region"
                />
              </div>
              <div className="min-w-[150px]">
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={setSelectedPremises}
                  type="premises"
                  selectedRegion={selectedRegion}
                />
              </div>
              <div className="min-w-[150px]">
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                >
                  <option value="ALL">All severities</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div className="min-w-[150px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                >
                  <option value="ALL">All status</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Under Investigation">Under Investigation</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <SpinLoader />
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <AlertTriangle className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No security incidents</p>
                <p className="text-sm text-center max-w-md">
                  Report an incident or trigger a panic test. Severities High/Critical require
                  evidence; all severities require escalation recipients.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="py-3 px-4 font-semibold text-gray-700">Number</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">Type</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">Location</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">When</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">Severity</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">Panic</th>
                      <th className="py-3 px-4 font-semibold text-gray-700">IM link</th>
                      <th className="py-3 px-4 font-semibold text-gray-700 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((inc) => {
                      const id = inc.id || inc._id;
                      return (
                        <tr key={id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-xs text-gray-800">
                            {inc.incidentNumber || id}
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {inc.incidentType}
                          </td>
                          <td className="py-3 px-4 text-gray-600">{inc.location}</td>
                          <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                            {inc.incidentDateTime
                              ? new Date(inc.incidentDateTime).toLocaleString()
                              : "—"}
                          </td>
                          <td className="py-3 px-4">
                            {getSeverityBadge(inc.severity as IncidentSeverity)}
                          </td>
                          <td className="py-3 px-4">{getStatusBadge(inc.status)}</td>
                          <td className="py-3 px-4 text-xs">
                            {inc.isPanicButton ? (
                              <span className="text-red-700 font-semibold">
                                {inc.panicSource === "wall_mounted" ? "Wall" : "Mobile"}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {inc.globalIncidentId ? (
                              <Link
                                to="/incidents"
                                className="text-[#0fa9a6] text-xs font-semibold hover:underline"
                              >
                                Open IM
                              </Link>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={() => openDetail(inc)}
                                className="p-2 text-[#0fa9a6] hover:bg-[#EAFBFA] rounded-lg transition-colors"
                                title="View"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative flex flex-col max-h-[85vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <SecurityIncidentForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              load();
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => setIsCreateModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="incidentForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Report incident
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailIncident(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative flex flex-col max-h-[90vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          {detailLoading || !detailIncident ? (
            <div className="flex justify-center py-20">
              <SpinLoader />
            </div>
          ) : (
            <>
              <SecurityIncidentForm
                isView
                initialData={mapSecurityIncidentToFormData(detailIncident)}
                prefillAttachments={evidenceToAttachments(detailIncident.evidence)}
                onSuccess={() => {
                  setIsDetailOpen(false);
                  setDetailIncident(null);
                  load();
                }}
                onCancel={() => {
                  setIsDetailOpen(false);
                  setDetailIncident(null);
                }}
              />
              <SecurityInvestigationPanel
                incident={detailIncident}
                orgUsers={orgUsersData}
                onUpdated={async () => {
                  const sid = detailIncident.id || detailIncident._id;
                  if (!sid) return;
                  const full = await getSecurityIncidentById(String(sid));
                  setDetailIncident(full);
                  load();
                }}
              />
            </>
          )}
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsDetailOpen(false);
              setDetailIncident(null);
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SecurityIncidents;
